import type { CompoundEntityRef } from '@backstage/catalog-model';
import type { FetchApi } from '@backstage/core-plugin-api';
import { NotFoundError, ResponseError } from '@backstage/errors';
import { TechDocsStorageClient } from '@backstage/plugin-techdocs';

type StorageClientOptions = ConstructorParameters<
  typeof TechDocsStorageClient
>[0];

const materialPaletteAttribute =
  /\sdata-md-color-(?:scheme|primary|accent)=(?:"[^"]*"|'[^']*')/gi;

const isHtmlPage = (path: string) => /(?:^|\/)[^/]+\.html$/i.test(path);

export function stripMaterialPaletteAttributes(html: string): string {
  return html.replace(/<body\b[^>]*>/i, body =>
    body.replace(materialPaletteAttribute, ''),
  );
}

export class OfflineCompatibleTechDocsStorageClient extends TechDocsStorageClient {
  private readonly offlineFetchApi: FetchApi;

  constructor(options: StorageClientOptions) {
    super(options);
    this.offlineFetchApi = options.fetchApi;
  }

  override async getEntityDocs(
    entityId: CompoundEntityRef,
    path: string,
  ): Promise<string> {
    if (!isHtmlPage(path)) {
      return stripMaterialPaletteAttributes(
        await super.getEntityDocs(entityId, path),
      );
    }

    const { kind, namespace, name } = entityId;
    const storageUrl = (await this.getStorageUrl()).replace(/\/+$/g, '');
    const request = await this.offlineFetchApi.fetch(
      `${storageUrl}/${namespace}/${kind}/${name}/${path}`,
    );

    if (request.status === 404) {
      throw new NotFoundError('Page not found. ');
    }

    if (request.status === 500) {
      throw new Error(
        'Could not generate documentation or an error in the TechDocs backend. ',
      );
    }

    if (!request.ok) {
      throw await ResponseError.fromResponse(request);
    }

    return stripMaterialPaletteAttributes(await request.text());
  }

  override async getBaseUrl(
    oldBaseUrl: string,
    entityId: CompoundEntityRef,
    path: string,
  ): Promise<string> {
    if (!isHtmlPage(path)) {
      return super.getBaseUrl(oldBaseUrl, entityId, path);
    }

    const { kind, namespace, name } = entityId;
    const apiOrigin = (await this.getApiOrigin()).replace(/\/+$/g, '');
    const parentPath = path.slice(0, path.lastIndexOf('/') + 1);
    const pageBaseUrl = `${apiOrigin}/static/docs/${namespace}/${kind}/${name}/${parentPath}`;

    return new URL(oldBaseUrl, pageBaseUrl).toString();
  }
}
